import DeleteIcon from "@mui/icons-material/DeleteRounded";
import { Box, Grid, IconButton, Stack } from "@mui/material";
import { FormikProps } from "formik";

import { CourtDayType, DayOfWeek, MonthlyOrdinal } from "@/api";
import { IOptionItem } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocomplete.types";
import { FormikAutocompleteSelect } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocompleteSelect";
import { CourtFormType } from "@/pages/system-settings/tabs/courts/vienna/components/court-dialog/court-form-fields/CourtForm.types";
import { useTranslation } from "@/translations";

import { RuleDayFields } from "../rule-day-fields/RuleDayFields";
import { SingleDatesDayFields } from "../single-dates-day-fields/SingleDatesDayFields";
import { WeekdayDayFields } from "../weekday-day-fields/WeekdayDayFields";

interface Props {
  formik: FormikProps<CourtFormType>;
  index: number;
  onRemove: () => void;
}

export function CourtDayRow({ formik, index, onRemove }: Props) {
  const { t } = useTranslation();
  const base = `courtDays.${index}` as const;
  const day = formik.values.courtDays[index];

  const typeOptions: IOptionItem[] = [
    { id: CourtDayType.Weekday, label: t("settings.courts.gerichtstag.types.weekday") },
    { id: CourtDayType.Rule, label: t("settings.courts.gerichtstag.types.rule") },
    { id: CourtDayType.SingleDates, label: t("settings.courts.gerichtstag.types.singleDates") },
  ];

  if (!day) {
    return null;
  }

  const handleTypeChange = (type: CourtDayType) => {
    void formik.setFieldValue(`${base}.type`, type);
    if (type === CourtDayType.Rule) {
      void formik.setFieldValue(`${base}.ordinal`, day.ordinal ?? MonthlyOrdinal.First);
      void formik.setFieldValue(`${base}.weekday`, day.weekday ?? DayOfWeek.Monday);
    } else if (type === CourtDayType.Weekday) {
      void formik.setFieldValue(`${base}.weekday`, day.weekday ?? DayOfWeek.Monday);
      void formik.setFieldValue(`${base}.ordinal`, null);
    } else {
      void formik.setFieldValue(`${base}.weekday`, null);
      void formik.setFieldValue(`${base}.ordinal`, null);
      if (day.dates.length === 0) {
        void formik.setFieldValue(`${base}.dates`, [null]);
      }
    }
  };

  return (
    <Box sx={{ bgcolor: (theme) => theme.palette.fsw["blue-5"], borderRadius: 1, p: 2 }}>
      <Stack direction="row" gap={2} alignItems="flex-start">
        <Grid container columnSpacing={2} rowSpacing={2} sx={{ flexGrow: 1 }}>
          <Grid size={4}>
            <FormikAutocompleteSelect
              formik={formik}
              path={`${base}.type`}
              label={t("settings.courts.gerichtstag.type")}
              options={typeOptions}
              onValueChange={(id) => {
                if (id) {
                  handleTypeChange(id as CourtDayType);
                }
              }}
            />
          </Grid>

          {day.type === CourtDayType.Weekday && <WeekdayDayFields formik={formik} index={index} />}
          {day.type === CourtDayType.Rule && <RuleDayFields formik={formik} index={index} />}
          {day.type === CourtDayType.SingleDates && <SingleDatesDayFields formik={formik} index={index} />}
        </Grid>

        <IconButton onClick={onRemove} size="medium" aria-label={t("settings.courts.gerichtstag.remove")}>
          <DeleteIcon />
        </IconButton>
      </Stack>
    </Box>
  );
}
